import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Logo } from "@/components/common/Logo";
import api, { humanError } from "@/lib/api";

/**
 * Set a new password from a emailed link.
 *
 * The rules are shown before the field is touched, not revealed by rejection,
 * and the server's specific reasons are listed when it refuses. A form that
 * says only "not strong enough" leaves people guessing.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);

  const mismatch = confirm.length > 0 && password !== confirm;

  async function onSubmit(e) {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setProblems([]);
    try {
      await api.post("/auth/password/reset", { token, password });
      toast.success("Password changed. Sign in with your new one.");
      nav("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.error;
      if (detail && typeof detail === "object" && detail.problems) {
        setProblems(detail.problems);
      } else {
        toast.error(humanError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="hs-mint-gradient flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-[#E4E9EF] bg-white p-8 text-center hs-card-shadow">
          <h1 className="font-display text-2xl font-bold text-[#0B2B33]">This link is incomplete</h1>
          <p className="mt-2 text-sm text-[#43535E]">
            Open the link from your email exactly as it was sent, or ask for a new one.
          </p>
          <Link to="/forgot-password" className="hs-btn-primary mt-6 inline-flex w-full items-center justify-center">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="hs-mint-gradient flex min-h-screen items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-[#E4E9EF] bg-white p-8 hs-card-shadow">
          <h1 className="font-display text-2xl font-bold text-[#0B2B33]">Set a new password</h1>
          <p className="mt-1 text-sm text-[#43535E]">Choose something you have not used elsewhere.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="pw">
                New password
              </label>
              <input
                id="pw" type="password" required autoFocus value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input" autoComplete="new-password"
                className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2.5 text-sm text-[#0B2B33] focus:border-[#1F5FD8] focus:outline-none focus:ring-2 focus:ring-[#1F5FD8]/20"
                placeholder="••••••••••"
              />
              <ul className="mt-2 space-y-1 text-xs text-[#8A97A0]">
                <li className={password.length >= 10 ? "text-[#16A46B]" : ""}>
                  {password.length >= 10 ? "✓" : "•"} At least 10 characters
                </li>
                <li>• Not a commonly used password</li>
                <li>• Does not contain your name or email address</li>
              </ul>
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]" htmlFor="pw2">
                Confirm
              </label>
              <input
                id="pw2" type="password" required value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input" autoComplete="new-password"
                className={`w-full rounded-lg border px-3 py-2.5 text-sm text-[#0B2B33] focus:outline-none focus:ring-2 ${
                  mismatch
                    ? "border-[#E3252A] focus:border-[#E3252A] focus:ring-[#E3252A]/20"
                    : "border-[#E4E9EF] focus:border-[#1F5FD8] focus:ring-[#1F5FD8]/20"
                }`}
                placeholder="••••••••••"
              />
              {mismatch ? (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-[#B02328]">
                  <X className="h-3.5 w-3.5" /> These do not match.
                </p>
              ) : confirm ? (
                <p className="mt-1.5 flex items-center gap-1 text-xs text-[#16A46B]">
                  <Check className="h-3.5 w-3.5" /> They match.
                </p>
              ) : null}
            </div>

            {problems.length ? (
              <div className="rounded-lg bg-[#FDECEC] p-3" role="alert" data-testid="reset-problems">
                <p className="text-xs font-semibold text-[#B02328]">Pick a different password:</p>
                <ul className="mt-1 space-y-0.5 text-xs text-[#B02328]">
                  {problems.map((p) => <li key={p}>• {p}</li>)}
                </ul>
              </div>
            ) : null}

            <button type="submit" disabled={busy || mismatch} data-testid="reset-submit"
              className="hs-btn-primary w-full disabled:opacity-60">
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        </div>
        <p className="mt-5 text-center text-sm text-[#43535E]">
          <Link to="/login" className="font-semibold text-[#1F5FD8] hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
